/**
 * DeptSwitcher — header dropdown to change active department.
 *
 * Shows: current department name + small role pill, with a caret indicating
 * the dropdown.  When the user has only one membership, renders as a static
 * label (no dropdown) — the picker UI shouldn't suggest a choice that doesn't exist.
 *
 * Switching departments triggers a reload-free state change: any data fetched
 * via X-Department-Id will refetch automatically on the next request because
 * the API client reads activeDepartmentId from the store on every call.
 */
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { ChevronDown, Check } from "lucide-react";

import { useAuthStore } from "../stores/authStore";
import { toast } from "../stores/toastStore";

export default function DeptSwitcher() {
  const memberships         = useAuthStore((s) => s.memberships);
  const activeDepartmentId  = useAuthStore((s) => s.activeDepartmentId);
  const setActiveDepartment = useAuthStore((s) => s.setActiveDepartment);

  const active = memberships.find((m) => m.department_id === activeDepartmentId);

  // Single membership → static label, no menu.
  if (memberships.length <= 1 || !active) {
    return (
      <div className="flex items-center gap-3">
        <span className="eyebrow !text-[var(--color-ink-3)]">DEPARTMENT</span>
        <span className="text-[var(--text-sm)] text-[var(--color-ink)] font-medium">
          {active?.department_name || "—"}
        </span>
        {active && (
          <span className="mono text-[10px] tracking-[0.16em] uppercase text-[var(--color-ink-3)]">
            {active.role}
          </span>
        )}
      </div>
    );
  }

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          className="
            flex items-center gap-2.5
            text-left
            outline-none focus-visible:outline-2 focus-visible:outline-[var(--color-accent)] focus-visible:outline-offset-2
          "
        >
          <span className="eyebrow !text-[var(--color-ink-3)]">DEPARTMENT</span>
          <span className="text-[var(--text-sm)] text-[var(--color-ink)] font-medium">
            {active.department_name}
          </span>
          <span className="mono text-[10px] tracking-[0.16em] uppercase text-[var(--color-ink-3)]">
            {active.role}
          </span>
          <ChevronDown size={12} className="text-[var(--color-ink-3)] ml-1" />
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="start"
          sideOffset={10}
          className="
            min-w-[16rem]
            bg-[var(--color-paper)]
            border border-[var(--color-rule-strong)]
            outline-none
            data-[state=open]:animate-[fadeIn_180ms_ease-out]
          "
        >
          <div className="px-4 py-3 border-b border-[var(--color-rule)]">
            <div className="eyebrow">SWITCH DEPARTMENT</div>
          </div>
          {memberships.map((m) => {
            const isActive = m.department_id === activeDepartmentId;
            return (
              <DropdownMenu.Item
                key={m.department_id}
                onSelect={() => {
                  if (isActive) return;
                  setActiveDepartment(m.department_id);
                  toast.success(`Now working in ${m.department_name}`);
                }}
                className="
                  px-4 py-3 outline-none cursor-pointer
                  flex items-center justify-between gap-3
                  data-[highlighted]:bg-[var(--color-paper-2)]
                "
              >
                <div>
                  <div className="text-[var(--text-sm)] text-[var(--color-ink)]">
                    {m.department_name}
                  </div>
                  <div className="mono text-[10px] tracking-[0.16em] uppercase text-[var(--color-ink-3)] mt-0.5">
                    {m.department_code} · {m.role}
                  </div>
                </div>
                {isActive && <Check size={14} className="text-[var(--color-accent)]" />}
              </DropdownMenu.Item>
            );
          })}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
